import React, {createContext, useState, useRef, ReactNode} from "react";
import axios from "axios";
import {AuthUser} from "../Models/DTOs/AuthUser";
import {GetAuthUser} from "../Api/Account";
import {GetUserSubscription} from "../Api/Subscription";
import {SubscriptionDTO} from "../Models/DTOs/Subscription";
import {hasContentAccess} from "../Helpers/Subscription";

// -----------------------------------------------------------------------------
// Storage keys
// -----------------------------------------------------------------------------
const RT_KEY = "authRefreshToken";
const RT_EXP_KEY = "authRefreshExp"; // epoch ms

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------
type TokenBundle = {
    accessToken: string;
    refreshToken: string;
    accessExpiresIn: number;    // seconds (e.g., 900)
    refreshExpiresIn: number;   // seconds (e.g., 1209600)
};

interface UserAuthenticationContextTypes {
    userData: AuthUser | null;
    userDataLoading: boolean;
    userDataFirstFetchComplete: boolean;
    // True from boot until the first auth bootstrap (refresh + user fetch)
    // settles. Lets UI avoid committing to a logged-out decision too early.
    authInitializing: boolean;
    // Changes on login/logout, not routine token rotation, invalidating playback grants.
    authSessionVersion: number;

    // Token presence/flow
    doesAuthTokenExist: () => boolean;
    storeTokens: (bundle: TokenBundle, rememberMe: boolean) => void;

    // Back-compat (noop shim around storeTokens – will only save AT to session/local)
    storeToken: (token: string, rememberMe: boolean) => void;

    // User data fetch / refresh
    fetchUserData: () => Promise<AuthUser | null>;
    // Corrects a freshly-loaded AuthUser's paused-in-grace entitlement (see
    // reconcilePausedEntitlement). Call before setUserData on login/signup paths
    // that fetch the user themselves instead of via fetchUserData.
    reconcileEntitlement: (me: AuthUser | null) => Promise<AuthUser | null>;
    refreshTokens: () => Promise<boolean>;
    refreshUserData: () => Promise<void>;
    // One-shot startup bootstrap with retry on transient failures.
    bootstrapAuth: () => Promise<void>;

    // Token accessors for components
    getAccessTokenSync: () => string | null;           // returns AT if still valid
    getValidAccessToken: () => Promise<string | null>; // refreshes if needed

    // Session control
    logOutUser: () => void;

    // Direct setter if needed
    setUserData: (user: AuthUser | null) => void;
}

// -----------------------------------------------------------------------------
// Context
// -----------------------------------------------------------------------------
export const UserAuthenticationContext = createContext<UserAuthenticationContextTypes>(
    {} as UserAuthenticationContextTypes
);

// -----------------------------------------------------------------------------
// Provider
// -----------------------------------------------------------------------------
export const UserAuthenticationContextProvider = ({children}: { children: ReactNode }) => {
    // Access token is memory-only
    const
        accessTokenRef = useRef<string | null>(null),
        accessExpMsRef = useRef<number>(0),
        // Single-flight guard for refreshTokens() — see performRefresh below.
        refreshInFlightRef = useRef<Promise<boolean> | null>(null),

        [userData, setUserData] = useState<AuthUser | null>(
            {
                id: "",
                subscription: {
                    status: 'none'
                }
            } as AuthUser
        ),
        [authSessionVersion, setAuthSessionVersion] = useState(0),
        [userDataLoading, setUserDataLoading] = useState<boolean>(false),
        [userDataFirstFetchComplete, setUserDataFirstFetchComplete] = useState<boolean>(false),

        // If a refresh token exists at boot we still have to validate it, so we
        // start in the "initializing" state and let bootstrapAuth() resolve it.
        // No token at boot means we're signed out and don't need a loading screen.
        [authInitializing, setAuthInitializing] = useState<boolean>(
            () => !!(window.localStorage.getItem(RT_KEY) ?? window.sessionStorage.getItem(RT_KEY))
        ),

        nowMs = () => Date.now();

    // Async callers can retain older context functions. Read and clear the current
    // profile through a ref so repeated checks cannot re-clear an expired session.
    const userDataRef = useRef(userData);
    userDataRef.current = userData;

    // --------------------------- Refresh Token Storage --------------------------
    const writeRT = (rt: string, expMs: number, remember: boolean) => {
        const keep = remember ? window.localStorage : window.sessionStorage;
        const other = remember ? window.sessionStorage : window.localStorage;
        keep.setItem(RT_KEY, rt);
        keep.setItem(RT_EXP_KEY, String(expMs));
        other.removeItem(RT_KEY);
        other.removeItem(RT_EXP_KEY);
    };

    const readRT = (): { rt: string | null; expMs: number; remembered: boolean } => {
        const lsRT = window.localStorage.getItem(RT_KEY);
        const ssRT = window.sessionStorage.getItem(RT_KEY);
        const rt = lsRT ?? ssRT ?? null;

        const lsExp = window.localStorage.getItem(RT_EXP_KEY);
        const ssExp = window.sessionStorage.getItem(RT_EXP_KEY);
        const expMsRaw = lsExp ?? ssExp ?? "0";
        const expMs = parseInt(expMsRaw, 10) || 0;

        return {rt, expMs, remembered: !!lsRT};
    };

    const clearRT = () => {
        window.localStorage.removeItem(RT_KEY);
        window.localStorage.removeItem(RT_EXP_KEY);
        window.sessionStorage.removeItem(RT_KEY);
        window.sessionStorage.removeItem(RT_EXP_KEY);
    };

    const setAccess = (token: string, ttlSeconds: number) => {
        const exp = nowMs() + ttlSeconds * 1000;
        accessTokenRef.current = token;
        accessExpMsRef.current = exp;
    };

    // ------------------------------ Public helpers -----------------------------
    const doesAuthTokenExist = () => {
        const validAT = !!accessTokenRef.current && accessExpMsRef.current > nowMs() + 1000;
        if (validAT) return true;
        const {rt, expMs} = readRT();
        return !!rt && expMs > nowMs() + 1000;
    };

    const storeTokens = (bundle: TokenBundle, rememberMe: boolean, preserveSession = false) => {
        if (!preserveSession) setAuthSessionVersion(version => version + 1);
        setAccess(bundle.accessToken, bundle.accessExpiresIn);
        writeRT(bundle.refreshToken, nowMs() + bundle.refreshExpiresIn * 1000, rememberMe);
    };

    // Back-compat: older code calling storeToken(AT only)
    const storeToken = (token: string, rememberMe: boolean) => {
        setAuthSessionVersion(version => version + 1);
        setAccess(token, 14 * 60);
    };

    const clearAuth = () => {
        const hadSession = !!accessTokenRef.current || !!readRT().rt || !!userDataRef.current?.id;
        accessTokenRef.current = null;
        accessExpMsRef.current = 0;
        clearRT();
        // Public playback also asks for tokens (for example, view analytics).
        // An already-anonymous check is not a logout and must not revoke its grant.
        if (!hadSession) return;
        userDataRef.current = null;
        setAuthSessionVersion(version => version + 1);
        setUserData(null);
    };

    // --------------------------- Token accessors --------------------------------
    const getAccessTokenSync = () => {
        const tok = accessTokenRef.current;
        if (!tok) return null;
        if (accessExpMsRef.current <= nowMs() + 1000) return null;
        return tok;
    };

    // Performs the actual refresh request. Do NOT call directly from multiple
    // places concurrently — go through refreshTokens(), which single-flights.
    const performRefresh = async (): Promise<boolean> => {
        const {rt, expMs, remembered} = readRT();
        if (!rt || expMs <= nowMs()) {
            clearAuth();
            return false;
        }
        try {
            const url = `${process.env.REACT_APP_AUTH_API_URL ?? ""}v1/refresh`;
            const res = await axios.post(url, {refresh_token: rt}, {timeout: 15000});
            // A refresh started by a previous login must not restore it after logout
            // or overwrite a newer account's credentials.
            if (readRT().rt !== rt) return false;
            const body = res.data ?? {};
            const data = body.data ?? body;

            const newAT: string | undefined = data?.access_token;
            const newRT: string | undefined = data?.refresh_token; // optional rotation
            const atTTL: number = data?.expires_in ?? 900;
            const rtTTL: number =
                data?.refresh_expires_in ??
                Math.max(0, Math.floor((expMs - nowMs()) / 1000));

            if (!newAT) throw new Error("missing access_token");

            // Use the same path so refs + state are kept in sync.
            storeTokens(
                {
                    accessToken: newAT,
                    refreshToken: newRT ?? rt,
                    accessExpiresIn: atTTL,
                    refreshExpiresIn: rtTTL,
                },
                remembered,
                true
            );
            return true;
        } catch (err: unknown) {
            // Only hard-clear the session when the server explicitly rejects the
            // refresh token (401/403). Transient/network errors must NOT log the
            // user out — keep the stored RT so a later attempt can recover.
            const status = axios.isAxiosError(err) ? err.response?.status : undefined;
            if ((status === 401 || status === 403) && readRT().rt === rt) {
                clearAuth();
            }
            return false;
        }
    };

    // Single-flight refresh. On a browser reload the in-memory access token is
    // gone, so every component that needs auth calls this at once. Without a
    // shared in-flight promise each would POST /v1/refresh with the same token;
    // with server-side refresh-token rotation the first wins and the rest get
    // 401, and a losing 401 would wipe the freshly-stored good session. Sharing
    // one request makes concurrent callers all await the same result.
    const refreshTokens = async (): Promise<boolean> => {
        if (refreshInFlightRef.current) return refreshInFlightRef.current;
        const p = performRefresh();
        refreshInFlightRef.current = p;
        try {
            return await p;
        } finally {
            refreshInFlightRef.current = null;
        }
    };

    const getValidAccessToken = async (): Promise<string | null> => {
        const at = getAccessTokenSync();
        if (at) return at;
        const ok = await refreshTokens();
        return ok ? getAccessTokenSync() : null;
        // If still null → caller should treat as signed-out
    };

    // The auth service's `is_entitled` flag is NOT pause-grace-aware: the moment a
    // member pauses an active subscription the flag flips to false, even though the
    // pause contract grants them access until their already-paid period ends. Every
    // content gate reads `is_entitled`, so reconcile it here — once, at the single
    // point user data is loaded — against the billing API's grace-aware
    // HasContentAccess. This restores full site access for paused-in-grace members
    // and correctly leaves it revoked once the freeze window begins. Best-effort:
    // any failure falls back to the auth service's flag.
    const reconcilePausedEntitlement = async (
        me: AuthUser | null,
        token: string
    ): Promise<AuthUser | null> => {
        if (
            !me?.subscription ||
            me.subscription.is_entitled ||
            (me.subscription.status || "").toLowerCase() !== "paused"
        ) {
            return me;
        }
        try {
            const sub = await GetUserSubscription(me.id, token, new AbortController());
            if (sub && typeof sub === "object" && !(sub as any).isAxiosError) {
                if (hasContentAccess(sub as SubscriptionDTO)) {
                    me.subscription.is_entitled = true;
                }
            }
        } catch {
            // non-fatal — keep the auth service's flag
        }
        return me;
    };

    // Public wrapper for callers (login/signup) that fetch the user themselves and
    // then setUserData directly, bypassing fetchUserData's reconciliation.
    const reconcileEntitlement = async (me: AuthUser | null): Promise<AuthUser | null> => {
        const token = getAccessTokenSync();
        if (!token) return me;
        return reconcilePausedEntitlement(me, token);
    };

    // --------------------------- User fetching ----------------------------------
    const fetchUserData = async (): Promise<AuthUser | null> => {
        setUserDataLoading(true);
        try {
            // Ensure an access token (refresh if needed)
            let token = getAccessTokenSync();
            if (!token) {
                const ok = await refreshTokens();
                // refreshTokens() only clears auth on a definitive 401/403; a
                // transient/network failure leaves the RT in place. Either way
                // we bail for this attempt and let bootstrapAuth() decide whether
                // to retry (transient) or stop (RT gone → signed out).
                if (!ok) return null;
                token = getAccessTokenSync();
                if (!token) return null;
            }

            const res = await GetAuthUser(token, new AbortController());
            if ((res as any)?.isAxiosError) {
                const status = (res as any)?.response?.status;
                // Only treat an explicit auth rejection as a reason to re-refresh.
                // Network/5xx/timeout → return null so the bootstrap can retry
                // without throwing away a session that is probably still valid.
                if (status === 401 || status === 403) {
                    const ok = await refreshTokens();
                    if (!ok) return null;
                    const token2 = getAccessTokenSync();
                    if (!token2) return null;
                    const res2 = await GetAuthUser(token2, new AbortController());
                    if ((res2 as any)?.isAxiosError) return null;
                    const me2 = res2 as AuthUser;
                    await reconcilePausedEntitlement(me2, token2);
                    setUserData(me2);
                    setUserDataFirstFetchComplete(true);
                    return me2;
                }
                return null;
            }

            const me = res as AuthUser;
            await reconcilePausedEntitlement(me, token);
            setUserData(me);
            setUserDataFirstFetchComplete(true);
            return me;
        } catch {
            // Unexpected (non-Axios) error — don't nuke the session here; a
            // genuine auth failure is surfaced via performRefresh's 401 handling.
            return null;
        } finally {
            setUserDataLoading(false);
        }
    };

    const refreshUserData = async () => {
        await fetchUserData();
    };

    // Startup bootstrap. Retries transient failures (slow/flaky mobile networks
    // are the main culprit) with backoff so a single late response doesn't leave
    // userData empty and make the app look signed out. Stops immediately once the
    // session is known-good (user loaded) or definitively gone (RT cleared).
    const BOOTSTRAP_RETRY_DELAYS_MS = [400, 1200, 3000];
    const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

    const bootstrapAuth = async (): Promise<void> => {
        try {
            for (let attempt = 0; ; attempt++) {
                const me = await fetchUserData();
                if (me) return;                                       // signed in
                if (!doesAuthTokenExist()) return;                    // signed out
                if (attempt >= BOOTSTRAP_RETRY_DELAYS_MS.length) return; // give up, keep RT
                await sleep(BOOTSTRAP_RETRY_DELAYS_MS[attempt]);      // transient → retry
            }
        } finally {
            setAuthInitializing(false);
        }
    };

    const logOutUser = () => {
        clearAuth();
    };

    // --------------------------- Context value ----------------------------------
    const value: UserAuthenticationContextTypes = {
        userData,
        userDataLoading,
        userDataFirstFetchComplete,
        authInitializing,
        authSessionVersion,

        doesAuthTokenExist,
        storeTokens,
        storeToken, // back-compat shim

        fetchUserData,
        reconcileEntitlement,
        refreshTokens,
        refreshUserData,
        bootstrapAuth,

        getAccessTokenSync,
        getValidAccessToken,

        logOutUser,
        setUserData,
    };

    return (
        <UserAuthenticationContext.Provider value={value}>
            {children}
        </UserAuthenticationContext.Provider>
    );
};
